import React, { useContext, useEffect, useMemo } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'

import { Div, P } from '@driftt/tide-core'

import { DateRange } from 'components/DateRangePicker'
import RefreshActivity from 'components/RefreshActivity'
import { driftLoadingStateBuilder } from 'components/States'
import { FilterPage } from 'context/filtersContext'
import { SortContext } from 'context/sortContext'
import { useDebouncedCallback } from 'hooks/useDebouncedCallback'
import { useEmptyComponent } from 'hooks/useEmptyComponent'
import { useInsightsFeed } from 'hooks/useInsightsFeed'
import { SessionMetrics } from 'hooks/useSessionMetrics'
import CCRMFeedCard from 'sections/CCRM/FeedCard'
import { segmentTrack, TrackType } from 'utils/tracking'

import styles from './styles.module.css'

const PAGE_SIZE = 10
const SCROLL_TRACKING_DEBOUNCE_MS = 300

interface CCRMFeedProps {
  accountId?: string
  contactId?: string
  scrollTargetId?: string
  onScroll?: (scrollOffset: number) => void
  sessionMetrics: SessionMetrics
  withRefresh?: boolean
  withFilters?: boolean
  includeAllTimeActivities?: boolean
  includeSortControl?: boolean
  emptyComponent?: () => React.ReactElement
  dateRange?: DateRange
  initialScrollY?: number
}

const CCRMFeed: React.FC<CCRMFeedProps> = ({
  accountId,
  contactId,
  scrollTargetId,
  onScroll,
  sessionMetrics,
  withRefresh = false,
  withFilters = true,
  includeAllTimeActivities = false,
  includeSortControl = true,
  emptyComponent,
  dateRange,
  initialScrollY
}) => {
  const { activitiesSort, activitiesDescending } = useContext(SortContext)

  const insightsFeed = useInsightsFeed(
    FilterPage.ACTIVITIES,
    activitiesSort,
    activitiesDescending,
    PAGE_SIZE,
    !includeAllTimeActivities || !!dateRange,
    false,
    withFilters,
    accountId,
    contactId,
    dateRange
  )
  const {
    data,
    size,
    setSize,
    isLoadingInitialData,
    hasLoadedInitialData,
    isLoadingMore,
    isReachingEnd
  } = insightsFeed.insights

  const EmptyComponent = useEmptyComponent()
  const LoadingComponent = useMemo(() => driftLoadingStateBuilder('Loading your feed'), [])

  const loadMore = () => {
    return !isReachingEnd && !isLoadingMore && !isLoadingInitialData && setSize((size ?? 0) + 1)
  }

  const debouncedScrollTrack = useDebouncedCallback((offset: number) => {
    onScroll && onScroll(offset)
    segmentTrack(TrackType.INSIGHT_FEED_SCROLL, {
      scrollOffset: offset,
      ui_version: 'CCRM',
      location: window.document.location
    })
  }, SCROLL_TRACKING_DEBOUNCE_MS)

  useEffect(() => {
    if (scrollTargetId) {
      const scrollTarget = document.getElementById(scrollTargetId)
      if (scrollTarget) {
        scrollTarget.onscroll = (e: any) => debouncedScrollTrack(e.target.scrollTop)
      }
    }
  }, [debouncedScrollTrack, scrollTargetId])

  const handleRefresh = () => {
    insightsFeed.refresh()
    // Set scrollY to the top when refreshing the insightFeed
    onScroll && onScroll(0)
  }

  return (
    <>
      {hasLoadedInitialData && withRefresh && (
        <div className={styles.refreshButtonContainer}>
          <RefreshActivity
            prevCount={insightsFeed.count}
            onClick={handleRefresh}
            filterPage={FilterPage.ACTIVITIES}
            withDateRange={!includeAllTimeActivities}
            accountId={accountId}
            fixedWhenScrolled
          />
        </div>
      )}
      {!isLoadingInitialData && data.length > 0 && (
        <>
          {includeSortControl && (
            <Div className={styles.sortIndicator}>
              <Div className={styles.sortLineBreak} />
              <P>
                Sort by <span className={styles.sortValue}>Most recent activity</span>
              </P>
            </Div>
          )}
          <InfiniteScroll
            dataLength={data.length}
            next={loadMore}
            hasMore={!isReachingEnd}
            scrollableTarget={scrollTargetId}
            initialScrollY={initialScrollY}
            loader={<LoadingComponent />}
            endMessage={<></>} // nothing for now, could be handy in the future
          >
            {data.map((insight, index) => (
              <CCRMFeedCard
                key={insight.id}
                insight={{
                  ...insight,
                  isFirstCard: index === 0
                }}
                countCardDwellTime={sessionMetrics.countCardDwellTime}
              />
            ))}
          </InfiniteScroll>
        </>
      )}
      {!isLoadingInitialData && data.length === 0 && (
        <Div className={styles.emptyView}>
          {emptyComponent ? emptyComponent() : <EmptyComponent />}
        </Div>
      )}
      {isLoadingInitialData && (
        <Div className={styles.emptyView}>
          <LoadingComponent />
        </Div>
      )}
    </>
  )
}

export default CCRMFeed
